import React from 'react';
import styled from '@2pulse/responsive';
import { Button } from '@app/components/form/buttons/Button';
import { normalize } from '@app/helpers';

// LA LIGNE D'UN ÉLÉMENT de la page « Ruptures » (Maxence, 1er nov. 2026 :
// « comme sur l'OS »). Un ingrédient trouvé par la recherche, un ingrédient en
// rupture, un produit ou un menu désactivé par ricochet : même ligne pour les
// quatre. Le nom à gauche — estompé quand l'élément est en rupture —, et à
// droite son état (la pastille « RUPTURE ») puis, quand l'écran le demande, la
// bascule : « Mettre en rupture » ou « Remettre en stock ». Composant de
// présentation PUR : il ne connaît ni le restaurant ni les mutations.

export interface LigneDeRuptureProps {
  nom: string;
  /** Sous le nom : la catégorie, le produit d'une variation, « Déjà en rupture »… */
  sousLigne?: string;
  enRupture: boolean;
  /**
   * La bascule de droite. Absente, la ligne ne porte que son état — les
   * produits et menus désactivés par ricochet ne se remettent pas en stock
   * un par un, c'est l'ingrédient qui commande.
   */
  onBasculer?: () => void;
  /** La mutation est en cours : le bouton tourne, un second tap est ignoré. */
  enCours?: boolean;
  testID?: string;
}

export const LigneDeRupture = ({ nom, sousLigne, enRupture, onBasculer, enCours = false, testID }: LigneDeRuptureProps) => (
  <Ligne testID={testID}>
    <Gauche>
      <Nom estompe={enRupture} numberOfLines={1}>{nom}</Nom>
      {sousLigne !== undefined && <SousLigne numberOfLines={1}>{sousLigne}</SousLigne>}
    </Gauche>

    <Droite>
      {enRupture && (
        <Pastille testID="rupture-pastille">
          <PastilleTexte>RUPTURE</PastilleTexte>
        </Pastille>
      )}
      {onBasculer !== undefined && (
        <Button
          small
          smallText
          bordered={!enRupture}
          title={enRupture ? 'Remettre en stock' : 'Mettre en rupture'}
          loading={enCours}
          onPress={onBasculer}
          testID={testID !== undefined ? `${testID}-bascule` : undefined} />
      )}
    </Droite>
  </Ligne>
);

export default LigneDeRupture;

const Ligne = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: normalize(56),
  paddingVertical: normalize(8),
  paddingHorizontal: normalize(16),
  borderRadius: normalize(8),
  backgroundColor: theme.colors.translucid,
  marginBottom: normalize(6),
}));

const Gauche = styled.View({
  flex: 1,
  marginRight: normalize(12),
});

const Nom = styled.Text<{ estompe: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(15),
  fontWeight: '600',
  color: theme.colors.babypowder,
  opacity: props.estompe ? 0.5 : 1,
}));

const SousLigne = styled.Text({
  fontFamily: 'Work Sans',
  fontSize: normalize(12),
  color: 'rgba(255,255,255,0.5)',
  marginTop: normalize(2),
});

const Droite = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: normalize(12),
});

const Pastille = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.crimson,
  borderRadius: normalize(999),
  paddingVertical: normalize(4),
  paddingHorizontal: normalize(10),
}));

const PastilleTexte = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(11),
  fontWeight: '700',
  letterSpacing: 1,
  color: theme.colors.babypowder,
}));
